import type { BottleneckReportContent, FreeResult } from '../../lib/apiClient';
import { computeOverallScore, getHpGrade } from '../../lib/assessment/content';
import {
  BOTTLENECK_EXPLANATIONS,
  BOTTLENECK_LABELS,
} from '../../lib/report/bottleneckCategories';
import { PrintCard, PrintLabel, PrintPage } from './printPages';

/*
  Keys rather than an index: the no-browser-database rule scans for `.from(`
  and cannot tell `Array.from` from a Supabase query builder.
*/
const LINE_KEYS = ['a', 'b', 'c'] as const;

/** Ruled lines to write on, as the printed document provides. */
function WriteLines({ count = 2 }: { count?: number }) {
  return (
    <div className="mt-1 flex flex-col" aria-hidden="true" data-testid="print-write-lines">
      {LINE_KEYS.slice(0, count).map((key) => (
        <span key={key} className="h-6 border-b border-border" />
      ))}
    </div>
  );
}

/** Production's three questions under each bottleneck category. */
const CATEGORY_REFLECTION = [
  'Where is this showing up most in my life right now?',
  'What has this pattern already cost me?',
  'What would change if I corrected this?',
];

/** Production's three reflection questions on the Pattern Mechanics page. */
const PATTERN_REFLECTION = [
  'Where does this false belief show up most strongly in my day-to-day behavior?',
  'What does this pattern protect me from? What would I have to face if I removed it?',
  'What evidence has this pattern already cost me?',
];

/** And its three before reassessment. */
const REASSESSMENT_REFLECTION = [
  'What specifically changed in my behavior over the last 30 days?',
  'What did I resist doing, and what did that cost me?',
  'What is my honest read on which bottleneck will show up most on my next assessment?',
];

/**
 * The Bottleneck report as production's **printed document**
 * (`report-print.tsx@11ca09a`).
 *
 * This is a different document from the on-screen report, not the same one with
 * margins: production paginates it into a cover and ten titled pages, and its
 * score legends are worded differently from the screen's. V2 previously printed
 * the screen report, which reflowed into one long column.
 *
 * **The legends really do differ, and that is production's, not a slip.** On
 * screen the four cards read `Higher = more active` / `Higher = stronger
 * readiness`; in print they read `Higher = more active friction`, `Higher =
 * stronger readiness`, `Higher = more depleted`, `Higher = greater clarity`.
 * Reproduced on each surface as production has it.
 */
const PRINT_SCORE_CARDS: Array<{
  key: 'bottleneckScore' | 'executionReadinessScore' | 'recoveryDeficitScore' | 'clarityScore';
  label: string;
  legend: string;
}> = [
  { key: 'bottleneckScore', label: 'Bottleneck Score', legend: 'Higher = more active friction' },
  {
    key: 'executionReadinessScore',
    label: 'Execution Readiness',
    legend: 'Higher = stronger readiness',
  },
  { key: 'recoveryDeficitScore', label: 'Recovery Gap', legend: 'Higher = more depleted' },
  { key: 'clarityScore', label: 'Clarity Score', legend: 'Higher = greater clarity' },
];

export function BottleneckReportPrint({
  result,
  content,
  preparedFor,
  previousResult = null,
}: {
  result: FreeResult;
  content: BottleneckReportContent;
  preparedFor: string | null;
  /** The caller's previous result, when there is one. */
  previousResult?: FreeResult | null;
}) {
  const overall = Math.round(computeOverallScore(result));
  const { grade, label: gradeLabel } = getHpGrade(overall);
  const completedDate = result.completedAt
    ? new Date(result.completedAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;
  const categories = result.topCategories.filter((c) => BOTTLENECK_EXPLANATIONS[c]);

  /*
    Production's Score Movement table. It prints the four metrics side by side
    with the previous assessment, and only when there is one to compare to.
  */
  const movement = previousResult
    ? PRINT_SCORE_CARDS.map((card) => ({
        label: card.label,
        now: result[card.key],
        before: previousResult[card.key],
      })).filter(
        (row): row is { label: string; now: number; before: number } =>
          typeof row.now === 'number' && typeof row.before === 'number',
      )
    : [];

  /* Production prints the date the customer becomes eligible: thirty days on. */
  const reassessDate = result.completedAt
    ? new Date(new Date(result.completedAt).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(
        'en-US',
        { month: 'long', day: 'numeric', year: 'numeric' },
      )
    : null;

  return (
    <div className="print-document" data-testid="print-document-bottleneck">
      {/* ── Cover ──────────────────────────────────────────────────────── */}
      <PrintPage variant="cover" testId="print-cover">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
          Bottleneck Assessment
        </p>
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Primary Performance Type
        </p>
        <p className="text-3xl font-extrabold leading-tight">{result.primaryType}</p>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          {content.whatThisMeans}
        </p>

        <hr className="my-8 border-border" />

        <dl className="flex flex-col gap-1 text-sm">
          {preparedFor ? (
            <div className="flex gap-2">
              <dt className="font-bold">Prepared for</dt>
              <dd data-testid="print-prepared-for">{preparedFor}</dd>
            </div>
          ) : null}
          {completedDate ? (
            <div className="flex gap-2">
              <dt className="font-bold">Completed</dt>
              <dd>{completedDate}</dd>
            </div>
          ) : null}
          <div className="flex gap-2">
            <dt className="font-bold">Report Type</dt>
            <dd>Full Paid Report</dd>
          </div>
        </dl>

        <p className="mt-10 text-xs leading-relaxed text-muted-foreground">
          This assessment is for personal development and coaching purposes only. It is not medical,
          psychological, or clinical advice. The insights and recommendations in this report are
          designed to support professional performance and personal growth.
        </p>
      </PrintPage>

      {/* ── Scores ─────────────────────────────────────────────────────── */}
      <PrintPage label="Your Assessment Results" title="Your Scores at a Glance">
        <PrintCard className="mb-5" testId="print-hp-score">
          <PrintLabel>HP Performance Score</PrintLabel>
          <p className="flex items-baseline gap-2">
            <span className="text-4xl font-bold tabular-nums" data-testid="print-hp-value">
              {overall}
            </span>
            <span className="text-sm text-muted-foreground">/100</span>
            <span className="ml-2 font-bold" data-testid="print-hp-grade">
              {grade}
            </span>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {gradeLabel}
            </span>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Composite rating across all four performance dimensions.
          </p>
        </PrintCard>

        <div className="grid grid-cols-2 gap-4">
          {PRINT_SCORE_CARDS.map((card) => {
            const value = result[card.key];
            if (typeof value !== 'number') return null;
            return (
              <div
                key={card.key}
                className="score-card rounded-lg border border-border p-4"
                data-testid="print-score-card"
                data-score={card.key}
              >
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {card.label}
                </p>
                <p className="text-2xl font-bold tabular-nums">{value}</p>
                <p className="score-direction text-xs italic text-muted-foreground">
                  {card.legend}
                </p>
              </div>
            );
          })}
        </div>
      </PrintPage>

      {movement.length > 0 ? (
        <PrintPage
          label="Since Your Last Assessment"
          title="What Changed"
          testId="print-score-movement"
        >
          <PrintLabel>Score Movement</PrintLabel>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="w-[42%] py-1 font-semibold">Metric</th>
                <th className="py-1 font-semibold">Previous</th>
                <th className="py-1 font-semibold">Now</th>
                <th className="py-1 font-semibold">Change</th>
              </tr>
            </thead>
            <tbody>
              {movement.map((row) => (
                <tr key={row.label} className="border-b border-border" data-testid="movement-row">
                  <td className="py-1">{row.label}</td>
                  <td className="py-1 tabular-nums">{row.before}</td>
                  <td className="py-1 tabular-nums">{row.now}</td>
                  <td className="py-1 tabular-nums">
                    {row.now > row.before ? '+' : ''}
                    {row.now - row.before}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </PrintPage>
      ) : null}

      {/* ── Category Breakdown ─────────────────────────────────────────── */}
      {categories.length > 0 ? (
        <PrintPage
          label="Section 06"
          title="Your Performance Bottlenecks"
          testId="print-category-breakdown"
        >
          <p className="mb-4 text-sm text-muted-foreground">
            These are the categories where your scores indicate the highest active drag on your
            performance. Address these first.
          </p>
          <div className="flex flex-col gap-4">
            {categories.map((category) => (
              <PrintCard key={category} testId="print-category">
                <p className="text-sm font-semibold">
                  {BOTTLENECK_LABELS[category] ?? category}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {BOTTLENECK_EXPLANATIONS[category]}
                </p>
                {/* Production gives every category the same three questions. */}
                <div className="mt-3 border-t border-border pt-3">
                  <PrintLabel>Reflection</PrintLabel>
                  {CATEGORY_REFLECTION.map((prompt) => (
                    <div key={prompt} className="mb-3">
                      <p className="text-sm italic leading-relaxed text-muted-foreground">
                        {prompt}
                      </p>
                      <WriteLines />
                    </div>
                  ))}
                </div>
              </PrintCard>
            ))}
          </div>
        </PrintPage>
      ) : null}

      {/* ── Deep Dive ──────────────────────────────────────────────────── */}
      <PrintPage label="Section 01" title={`Deep Dive. ${result.primaryType}`}>
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>What This Pattern Looks Like</PrintLabel>
            <p className="text-sm leading-relaxed">{content.fullExplanation}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>Why This Develops</PrintLabel>
            <p className="text-sm leading-relaxed">{content.whyThisDevelops}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>The Hidden Cost</PrintLabel>
            <p className="text-sm leading-relaxed">{content.hiddenCost}</p>
          </PrintCard>
        </div>
      </PrintPage>

      {/* ── Pattern Mechanics ──────────────────────────────────────────── */}
      <PrintPage label="Section 02" title="Pattern Mechanics">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>The False Belief</PrintLabel>
            <p className="text-sm leading-relaxed">{content.falseBelief}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>How You Sabotage Progress</PrintLabel>
            <p className="text-sm leading-relaxed">{content.sabotagePattern}</p>
          </PrintCard>
          <div className="truth-card rounded-lg border-l-4 border-primary p-4">
            <PrintLabel>The Truth You Need to Hear</PrintLabel>
            <p className="text-sm italic leading-relaxed">{content.truthToHear}</p>
          </div>
        </div>

        <div className="mt-4" data-testid="print-pattern-reflection">
          <PrintLabel>Reflection on Your Pattern</PrintLabel>
          {PATTERN_REFLECTION.map((prompt) => (
            <div key={prompt} className="mb-4">
              <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
              <WriteLines />
            </div>
          ))}
        </div>
      </PrintPage>

      {/* ── What You Need Most ─────────────────────────────────────────── */}
      <PrintPage label="Section 03" title="What You Need Most Right Now">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>Your Primary Intervention</PrintLabel>
            <p className="text-sm leading-relaxed">{content.whatYouNeedMost}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>What Changes When You Address This</PrintLabel>
            <p className="text-sm leading-relaxed">{content.whatChanges}</p>
          </PrintCard>
        </div>
      </PrintPage>

      {/* ── Stop / Start ───────────────────────────────────────────────── */}
      <PrintPage label="Section 04" title="Stop Doing / Start Doing">
        <div className="grid grid-cols-2 gap-4">
          <div className="stop-card rounded-lg border border-border p-4" data-testid="print-stop">
            <PrintLabel>Stop Doing</PrintLabel>
            <ul className="flex flex-col gap-2">
              {content.stopDoing.map((item) => (
                <li key={item} className="text-sm leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="start-card rounded-lg border border-border p-4" data-testid="print-start">
            <PrintLabel>Start Doing</PrintLabel>
            <ul className="flex flex-col gap-2">
              {content.startDoing.map((item) => (
                <li key={item} className="text-sm leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </PrintPage>

      {/* ── Habits and Warnings ────────────────────────────────────────── */}
      <PrintPage label="Section 05" title="Recommended Habits">
        <div className="grid grid-cols-2 gap-4">
          <PrintCard testId="print-habits">
            <PrintLabel>Recommended Habits</PrintLabel>
            <ul className="flex flex-col gap-2">
              {content.recommendedHabits.map((item) => (
                <li key={item} className="text-sm leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </PrintCard>
          <PrintCard testId="print-warnings">
            <PrintLabel>Warning Signals</PrintLabel>
            <ul className="flex flex-col gap-2">
              {content.warningSignals.map((item) => (
                <li key={item} className="text-sm leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </PrintCard>
        </div>
        <div className="mt-4">
          <PrintCard testId="print-reflection">
            <PrintLabel>Reflection Prompts</PrintLabel>
            <ul className="flex flex-col gap-2">
              {content.reflectionPrompts.map((item) => (
                <li key={item} className="text-sm italic leading-relaxed text-muted-foreground">
                  {item}
                </li>
              ))}
            </ul>
          </PrintCard>
        </div>
      </PrintPage>

      {/* ── Reset Plan ─────────────────────────────────────────────────── */}
      <PrintPage label="Section 07" title="Your Reset Plan">
        <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
          The diagnostic work is complete. What follows is execution. Your reset plan consists of
          two separate printable documents, each personalized to your type. Open them from the
          buttons below.
        </p>
        <div className="flex flex-col gap-4">
          {content.sevenDayPlan.map((day) => (
            <PrintCard key={day.day} testId="print-day">
              <PrintLabel>Day {day.day}</PrintLabel>
              <p className="text-sm font-semibold">{day.focus}</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{day.objective}</p>
            </PrintCard>
          ))}
          {content.thirtyDayPlan.map((week) => (
            <PrintCard key={week.week} testId="print-week">
              <PrintLabel>Week {week.week}</PrintLabel>
              <p className="text-sm font-semibold">{week.theme}</p>
              <ul className="mt-1 flex flex-col gap-1">
                {week.goals.map((goal) => (
                  <li key={goal} className="text-sm leading-relaxed text-muted-foreground">
                    {goal}
                  </li>
                ))}
              </ul>
            </PrintCard>
          ))}
        </div>
      </PrintPage>

      {/* ── Reassessment ───────────────────────────────────────────────── */}
      <PrintPage
        label="Section 08"
        title="Reassessment Guidance"
        variant="final-page"
        testId="print-reassessment"
      >
        <div className="reassess-box mb-4 flex flex-col gap-3">
          {/* Production heads the date, when it has one. */}
          {reassessDate ? (
            <div data-testid="print-reassess-date">
              <PrintLabel>You are eligible to reassess on</PrintLabel>
              <p className="text-base font-bold">{reassessDate}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Retaking before 30 days have passed will not produce a meaningful signal. Your
                patterns need time to shift.
              </p>
            </div>
          ) : null}
          <p className="text-sm leading-relaxed">{content.nextBestStep}</p>
        </div>

        <PrintCard className="mb-4">
          <PrintLabel>What to expect on reassessment</PrintLabel>
          <p className="text-sm leading-relaxed">
            Your reassessment will produce a new set of scores across the same four dimensions.
            Compare them directly to the scores in this report. A meaningful shift is 5 or more
            points in any dimension. Small changes under 5 points are within the normal range and do
            not indicate progress or regression.
          </p>
        </PrintCard>

        <PrintCard className="mb-4" testId="print-how-scores-move">
          <PrintLabel>How scores move</PrintLabel>
          <p className="text-sm leading-relaxed">
            Bottleneck Score and Recovery Deficit are lower-is-better metrics. A drop means
            improvement. Execution Readiness and Clarity Score are higher-is-better. A rise means
            improvement. HP Performance Score rises as the other scores move in the right direction.
          </p>
        </PrintCard>

        <PrintCard className="mb-4">
          <PrintLabel>What clearing one bottleneck reveals</PrintLabel>
          <p className="text-sm leading-relaxed">
            Every bottleneck you address exposes the next layer of friction underneath it. Your
            primary type may shift on reassessment. This is expected and indicates the work you did
            in this reset cycle had an effect. Your new report will identify the next
            highest-priority pattern to address.
          </p>
        </PrintCard>

        <div data-testid="print-reassessment-reflection">
          <PrintLabel>Reflection before reassessment</PrintLabel>
          {REASSESSMENT_REFLECTION.map((prompt) => (
            <div key={prompt} className="mb-4">
              <p className="text-sm italic leading-relaxed text-muted-foreground">{prompt}</p>
              <WriteLines count={3} />
            </div>
          ))}
        </div>
      </PrintPage>
    </div>
  );
}
